/** Public types of the live bookcase viewer (`mountBookcase`). */
import type { BoardAssembly } from '@/families/assembly';
import type { ThemeName } from './materials';

/**
 * What the live viewer reads: the family-agnostic BoardAssembly (parts with `material`,
 * `stockId`, `grainWorld` and placement; openings with bounds; envelope). The grid family's
 * BuildModel also carries `thickness`; other families' thicknesses are read from the parts.
 */
export type ViewerBuild = Pick<BoardAssembly, 'parts' | 'openings' | 'envelope'> & Partial<Pick<BoardAssembly, 'hardware' | 'joints'>> & { thickness?: { carcass: number; back: number } };

/** Carcass and back thickness (mm) of a build: its own, else from the parts. */
export function thicknessesOf(b: ViewerBuild): { carcass: number; back: number } {
  if (b.thickness) return b.thickness;
  const back = b.parts.find((p) => p.role === 'back');
  const carcass = b.parts.find((p) => p.role !== 'back');
  return { carcass: (carcass?.thicknessTicks ?? 180) / 10, back: (back?.thicknessTicks ?? 0) / 10 };
}

export type { ThemeName };

export type ViewName = 'front' | 'three-quarter' | 'side' | 'top' | 'edge-detail';

export interface ViewerInset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ViewerOptions {
  theme?: ThemeName;
  /** 'transparent' (default) or a CSS colour. */
  background?: string | null;
  /** Highlight colour (default: the theme's calm blue). */
  accent?: string | null;
  showDims?: boolean;
  scaleFigure?: boolean;
  props?: boolean;
  exploded?: boolean;
  /** Doors swung open, drawers pulled out, a sliding panel slid across (Look; appearance only). */
  frontsOpen?: boolean;
  /** The kit's Inspect LOD (authored details) on the assembled model; exploded implies it. */
  detail?: boolean;
  /** Wall + floor staging instead of the shadow-catcher floor. */
  backdrop?: boolean;
  /** px of stage overlay to frame the model around. */
  inset?: Partial<ViewerInset> | null;
  /** Mount-time only. */
  interactive?: boolean;
  view?: ViewName;
  onPick?: ((partId: string | null) => void) | null;
  onHover?: ((partId: string | null) => void) | null;
  onOpeningPick?: ((index: number, additive: boolean) => void) | null;
  onOpeningHover?: ((index: number | null) => void) | null;
  onCameraChange?: (() => void) | null;
  onContextLost?: ((isLost: boolean) => void) | null;
  onReady?: (() => void) | null;
  /**
   * Look (plan §3 step 4): every part's materials come from the Blender kit by its `stockId` and
   * `material` (birch ply, sheesham, oak, with matching edges and end grain); `finishId` picks the
   * finish (null: the stock's own, solid oiled / ply matt) and `showHardware` shows the fixings.
   */
  appearance?: { materialId: string; stockId: string; finishId: 'oiled' | 'satin' | 'matt' | null; showHardware: boolean } | null;
}

export interface SnapshotOptions {
  width?: number;
  height?: number;
  quality?: 'preview' | 'finished';
  view?: ViewName | null;
  background?: string;
  backdrop?: boolean;
  mime?: string;
  samples?: number;
}

/** A part id, an opening (0-based) or a point in build millimetres (origin floor / left / back). */
export type ScreenTarget = string | { opening: number } | { point: { x: number; y: number; z: number } };

export interface ScreenPosition {
  /** Host CSS px. */
  x: number;
  y: number;
  visible: boolean;
}

export interface FinishedModelInfo {
  /** Each part id found in the GLB node extras, once, in document order. */
  partIds: string[];
  /** Ids carried by more than one node (the model still loads). */
  duplicates: string[];
}

export interface BookcaseViewer {
  update(build: ViewerBuild, opts?: { animate?: boolean }): number;
  select(partId: string | null): void;
  hover(partId: string | null): void;
  highlightOpening(index: number | null): void;
  setView(name: ViewName, opts?: { animate?: boolean }): void;
  setTheme(name: ThemeName): void;
  setOptions(next: Partial<ViewerOptions>): void;
  /** Resolves to a data URL. Serialised; the live loop pauses while one runs. */
  snapshot(opts?: SnapshotOptions): Promise<string>;
  resize(): void;
  dispose(): void;
  getScreenPosition(target: ScreenTarget): ScreenPosition | null;
  /** Subscribe to every rendered frame (to reposition HTML overlays). Returns an unsubscribe function. */
  onFrame(fn: () => void): () => void;
  /**
   * Load a finished GLB (Blender export) in place of the live panels. Part nodes carry
   * `extras.partId`; `staging.*` nodes are hidden and never picked or framed. Rejects when
   * the GLB has no partId extras (the live model stays). A later load or unload supersedes
   * a pending one (the pending promise rejects).
   */
  loadFinishedModel(url: string): Promise<FinishedModelInfo>;
  /** Dispose the finished GLB and show the live model again. No-op when none is loaded. */
  unloadFinishedModel(): void;
  readonly finishedModelLoaded: boolean;
  readonly canvas: HTMLCanvasElement;
  readonly lastFrameMs: number;
  readonly lastUpdateMs: number;
  readonly view: ViewName;
  readonly selected: string | null;
  readonly hovered: string | null;
  /** Where the materials come from: the Blender kit, or flat colours if it could not load. */
  readonly textureSource: 'loading' | 'kit' | 'fallback';
  readonly theme: ThemeName;
  /** Resolves when the face textures have loaded (or fallen back to procedural). */
  readonly ready: Promise<void>;
  /** Internal objects for debugging only (not part of the contract). */
  readonly _debug: { scene: unknown; camera: unknown; renderer: unknown; panels: unknown; controls: unknown; layer: unknown };
}
